import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Quotation as QuotationUI, QuotationPage, QuotationBlock } from '@/components/quotation/types';
import { quotationService, Quotation as QuotationDB } from '@/lib/supabase/quotations';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { formatSafeDate } from '@/lib/date-utils';

const QUOTATION_STORAGE_KEY = 'voah_quotation_draft';
const SAVED_QUOTATIONS_KEY = 'voah_quotations';

// Variável global para armazenar a função moveFlightEventToPage
let globalMoveFlightEventToPage: ((sourcePageId: string, flightEventId: string, targetPageId: string) => void) | null = null;

// Variável global para armazenar a função moveAccommodationEventToPage
let globalMoveAccommodationEventToPage: ((sourcePageId: string, accommodationEventId: string, targetPageId: string) => void) | null = null;

// Near the top of the file, add a global variable for moveAccommodationToPage
let globalMoveAccommodationToPageRef: ((sourcePageId: string, accommodationId: string, targetPageId: string) => void) | null = null;

// Adicionar no início do arquivo, junto com outras variáveis globais
let globalMoveTransportationToPageRef: ((sourcePageId: string, transportationId: string, targetPageId: string) => boolean) | null = null;

// Adicionar a variável global para moveFlightToPage
let globalMoveFlightToPageRef: ((sourcePageId: string, flightId: string, targetPageId: string) => void) | null = null;

// Adicionar a variável global para moveBlockToPage
let globalMoveBlockToPageRef: ((sourcePageId: string, blockId: string, targetPageId: string) => void) | null = null;

export function useQuotationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;
  const [isLoading, setIsLoading] = useState(true);

  // Utilizamos o tipo QuotationUI que é o formato interno da aplicação
  // 1. Garantir que page_groups é carregado corretamente
  const [quotation, setQuotation] = useState<QuotationUI>({
    id: isEditing ? id! : `quotation_${Date.now()}`,
    title: '',
    client: '',
    destination: [],
    startDate: null,
    endDate: null,
    status: 'draft',
    notes: '',
    pages: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    tripId: undefined,
    showRecommendationsTab: true,
    showPriceTab: true,
    recommendations: [],
    recommendationGroups: [],
    page_groups: { groups: [], ungrouped: [] } // Fallback seguro
  });

  // Carregar dados da cotação do Supabase quando iniciar
  useEffect(() => {
    const loadQuotation = async () => {
      if (isEditing && id) {
        try {
          setIsLoading(true);
          
          // Buscar cotação do Supabase
          const quotationData = await quotationService.getQuotationById(id);
          
          if (quotationData) {
            // Logging removed: Cotação carregada do Supabase
            
            // Mapear os dados do formato do Supabase para o formato da aplicação
            const mappedQuotation: QuotationUI = {
              id: quotationData.id,
              title: quotationData.title || '',
              client: quotationData.client || '',
              destination: quotationData.destination || [],
              startDate: quotationData.start_date ? new Date(quotationData.start_date) : null,
              endDate: quotationData.end_date ? new Date(quotationData.end_date) : null,
              status: 'draft', // Valor padrão para status
              notes: quotationData.notes || '',
              pages: quotationData.pages || [],
              coverImages: quotationData.cover_images || [],
              createdAt: quotationData.created_at ? new Date(quotationData.created_at) : new Date(),
              updatedAt: quotationData.updated_at ? new Date(quotationData.updated_at) : new Date(),
              tripId: quotationData.trip_id,
              // Configurações de abas
              showRecommendationsTab: quotationData.show_recommendations_tab !== undefined 
                ? quotationData.show_recommendations_tab 
                : true,
              showPriceTab: quotationData.show_price_tab !== undefined 
                ? quotationData.show_price_tab 
                : true,
              recommendationsTabName: quotationData.recommendations_tab_name,
              // Adicionar configurações faltantes para abas
              showAccommodationsTab: quotationData.show_accommodations_tab !== false,
              accommodationsTabName: quotationData.accommodations_tab_name,
              showFlightsTab: quotationData.show_flights_tab !== false,
              flightsTabName: quotationData.flights_tab_name,
              priceTabName: quotationData.price_tab_name,
              // Outras configurações
              recommendationGroups: quotationData.recommendation_groups || [],
              useDayMode: quotationData.use_day_mode,
              usePageDropdown: quotationData.use_page_dropdown,
              showMapLines: quotationData.show_map_lines,
              showAccommodationsInItinerary: quotationData.show_accommodations_in_itinerary,
              showFlightsInItinerary: quotationData.show_flights_in_itinerary,
              // Campos de acomodações
              accommodationGroups: quotationData.accommodation_groups || [],
              useCustomAccommodationGroups: quotationData.use_custom_accommodation_groups || false,
              accommodationsDescription: quotationData.accommodations_description || '',
              showAccommodationsMapSection: quotationData.show_accommodations_map_section,
              // Campos de voos
              flightGroups: quotationData.flight_groups || [],
              useCustomFlightGroups: quotationData.use_custom_flight_groups || false,
              flightsDescription: quotationData.flights_description || '',
              // === Mapear campos da Aba Personalizada (Supabase -> UI) ===
              showCustomTab: quotationData.show_custom_tab !== undefined ? quotationData.show_custom_tab : false,
              customTabName: quotationData.custom_tab_name || 'Personalizada',
              customTabGroups: quotationData.custom_tab_groups || [],
              // =========================================================
              // Adicionar o campo priceDetails que estava faltando
              priceDetails: quotationData.price_details || '',
              // 2. No loadQuotation, garantir que page_groups é lido do banco/localStorage
              page_groups: quotationData.page_groups || { groups: [], ungrouped: quotationData.pages?.map(p => p.id) || [] },
            };
            
            // Logging removed: Dados dos grupos de recomendações carregados
            
            // Verificar se há dados no localStorage
            const storedQuotation = localStorage.getItem(QUOTATION_STORAGE_KEY);
            let finalQuotation = { ...mappedQuotation };

            if (storedQuotation) {
              try {
                const localStorageData = JSON.parse(storedQuotation);
                
                // Verificar se os IDs batem
                if (localStorageData.id === quotationData.id) {
                  // Verificar qual versão é mais recente
                  const localUpdatedAt = new Date(localStorageData.updatedAt);
                  const dbUpdatedAt = new Date(quotationData.updated_at);
                  
                  // Logging removed: Comparando timestamps
                  
                  // FASE 1: Verificar campos críticos (como notas) antes de decidir qual fonte usar
                  const hasDbNotes = quotationData.notes && quotationData.notes.trim() !== '';
                  const hasLocalNotes = localStorageData.notes && localStorageData.notes.trim() !== '';
                  
                  // Logging removed: Local storage notes info
                    
                  // FASE 2: Tomar decisão de mesclagem
                  if (localUpdatedAt > dbUpdatedAt) {
                    // Logging removed: Usando dados do localStorage (mais recentes)
                    
                    // CORREÇÃO: Sempre preservar notas do banco quando existirem, independente da temporalidade
                    // Criar uma versão mesclada priorizando campos críticos do banco
                    finalQuotation = {
                      ...localStorageData,  // Base do localStorage
                      // Preservar campos críticos do banco (sobrescrever localStorage)
                      notes: hasDbNotes ? quotationData.notes : localStorageData.notes,
                      // Preservar detalhamento de preços do banco se existir
                      priceDetails: quotationData.price_details || localStorageData.priceDetails || '',
                      // Preservar grupos de recomendação do banco se existirem
                      recommendationGroups: (quotationData.recommendation_groups && 
                        quotationData.recommendation_groups.length > 0) ? 
                        quotationData.recommendation_groups : localStorageData.recommendationGroups,
                      // Preservar grupos de acomodação do banco se existirem
                      accommodationGroups: (quotationData.accommodation_groups && 
                        quotationData.accommodation_groups.length > 0) ? 
                        quotationData.accommodation_groups : localStorageData.accommodationGroups,
                      // Preservar grupos de voo do banco se existirem
                      flightGroups: (quotationData.flight_groups && 
                        quotationData.flight_groups.length > 0) ? 
                        quotationData.flight_groups : localStorageData.flightGroups,
                      // 3. Garantir que page_groups seja lido do banco/localStorage
                      page_groups: localStorageData.page_groups || mappedQuotation.page_groups || { groups: [], ungrouped: mappedQuotation.pages?.map(p => p.id) || [] },
                    };
                    
                    // Logging removed: Mesclagem de dados realizada
                  } else {
                    // Logging removed: Usando dados do banco (mais recentes)
                  }
                  
                  // Logging removed: localStorage atualizado para refletir dados mesclados
                }
              } catch (error) {
                // Erro ao analisar JSON do localStorage - continuar com os dados do banco
              }
            }
            
            // Logging removed: Salvando no localStorage com notas

            // === MIGRAÇÃO AUTOMÁTICA: Adicionar páginas órfãs ao page_groups ===
            // Verificar se há páginas que existem mas não estão no page_groups
            const allPageIds = finalQuotation.pages?.map(p => p.id) || [];
            const currentPageGroups = finalQuotation.page_groups || { groups: [], ungrouped: [] };
            
            // IDs já alocados em grupos
            const groupedIds = (currentPageGroups.groups || []).flatMap(g => g.pageIds || []);
            const ungroupedIds = currentPageGroups.ungrouped || [];
            const allKnownIds = [...groupedIds, ...ungroupedIds];
            
            // Encontrar páginas órfãs (existem em pages mas não estão no page_groups)
            const orphanPageIds = allPageIds.filter(id => !allKnownIds.includes(id));
            
            // Se há páginas órfãs, adicioná-las automaticamente ao ungrouped
            if (orphanPageIds.length > 0) {
              console.log(`[Migration] Encontradas ${orphanPageIds.length} páginas órfãs, adicionando ao page_groups:`, orphanPageIds);
              
              finalQuotation.page_groups = {
                ...currentPageGroups,
                ungrouped: [...ungroupedIds, ...orphanPageIds]
              };
            }
            // ================================================================

            // Aplicar dados finais à cotação
            setQuotation(finalQuotation);
          } else {
            toast({
              title: "Cotação não encontrada",
              description: "Não foi possível encontrar a cotação com o ID fornecido.",
              variant: "destructive"
            });
            navigate('/admin/travel-hub');
          }
        } catch (error) {
          console.error("Erro ao carregar cotação:", error);
          toast({
            title: "Erro ao carregar",
            description: "Ocorreu um erro ao carregar a cotação do banco de dados.",
            variant: "destructive"
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    
    loadQuotation();
  }, [id, isEditing, navigate, toast]);

  // Estado para armazenar a versão anterior da cotação (para undo)
  const [previousQuotation, setPreviousQuotation] = useState<QuotationUI | null>(null);
  
  // Flag para indicar se a operação de desfazer está disponível
  const [canUndo, setCanUndo] = useState(false);

  // Função para atualizar o estado com backup para undo
  const updateQuotationWithBackup = (newQuotation: QuotationUI | ((prev: QuotationUI) => QuotationUI)) => {
    // Store current state in history for undo
    setPreviousQuotation(quotation);
    setCanUndo(true);
    
    if (typeof newQuotation === 'function') {
      setQuotation(prev => {
        const updated = newQuotation(prev);
        return updated;
      });
    } else {
      setQuotation(newQuotation);
    }
  };

  // Efeito para salvar automaticamente no localStorage quando a cotação mudar
  useEffect(() => {
    if (!isLoading && quotation) {
      const timeoutId = setTimeout(() => {
        console.log(`[useQuotationForm] Salvando no localStorage com notas: ${quotation.notes ? 
          (quotation.notes.length > 20 ? 'tamanho: ' + quotation.notes.length + ' chars' : quotation.notes) : 
          'vazio'}`);
        localStorage.setItem(QUOTATION_STORAGE_KEY, JSON.stringify({ ...quotation, page_groups: quotation.page_groups || { groups: [], ungrouped: quotation.pages?.map(p => p.id) || [] } }));
        console.log('Rascunho salvo no localStorage automaticamente (com debounce)');
      }, 1000); // Aguarda 1 segundo após a última alteração
      
      // Limpar o timeout anterior quando o estado mudar novamente antes do tempo
      return () => clearTimeout(timeoutId);
    }
  }, [quotation, isLoading]);

  // Função para desfazer a última ação
  const undo = () => {
    if (previousQuotation) {
      setQuotation(previousQuotation);
      setPreviousQuotation(null);
      setCanUndo(false);
    }
  };

  // === FUNÇÃO DE MIGRAÇÃO: Para corrigir cotações já carregadas ===
  const migrateOrphanPages = () => {
    const allPageIds = quotation.pages?.map(p => p.id) || [];
    const currentPageGroups = quotation.page_groups || { groups: [], ungrouped: [] };
    
    // IDs já alocados em grupos
    const groupedIds = (currentPageGroups.groups || []).flatMap(g => g.pageIds || []);
    const ungroupedIds = currentPageGroups.ungrouped || [];
    const allKnownIds = [...groupedIds, ...ungroupedIds];
    
    // Encontrar páginas órfãs (existem em pages mas não estão no page_groups)
    const orphanPageIds = allPageIds.filter(id => !allKnownIds.includes(id));
    
    // Se há páginas órfãs, adicioná-las automaticamente ao ungrouped
    if (orphanPageIds.length > 0) {
      console.log(`[migrateOrphanPages] Encontradas ${orphanPageIds.length} páginas órfãs, adicionando ao page_groups:`, orphanPageIds);
      
      const updatedPageGroups = {
        ...currentPageGroups,
        ungrouped: [...ungroupedIds, ...orphanPageIds]
      };
      
      handleQuotationChange('page_groups', updatedPageGroups);
      
      toast({
        title: "Páginas recuperadas",
        description: `${orphanPageIds.length} página(s) foram automaticamente adicionadas ao sistema de navegação.`,
      });
      
      return true; // Retorna true se houve migração
    }
    
    return false; // Retorna false se não havia páginas órfãs
  };
  // ================================================================

  // Manipulação dos dados da cotação
  const handleQuotationChange = (field: keyof QuotationUI, value: any) => {
    // Proteção para o campo notes: não sobrescrever por vazio se já existe valor
    if (
      field === 'notes' &&
      (value === '' || (typeof value === 'string' && value.trim() === '')) &&
      quotation.notes &&
      quotation.notes.trim() !== ''
    ) {
      // Apenas log para debug, pode remover depois
      console.warn('[PATCH] Tentativa de sobrescrever notes não-vazio por vazio bloqueada.');
      return;
    }
    
    // Special handling for useDayMode toggle
    if (field === 'useDayMode') {
      updateQuotationWithBackup(prev => {
        const updatedQuotation = { ...prev, [field]: value, updatedAt: new Date() };
        
        // If switching to day mode, update all pages with appropriate day titles
        if (value === true) {
          updatedQuotation.pages = updatedQuotation.pages.map((page, index) => {
            let dayTitle = `Dia ${index + 1}`;
            
            // If we have a defined start date, calculate sequential dates
            if (updatedQuotation.startDate && index === 0) {
              // First day gets the exact start date
              dayTitle = `Dia ${index + 1} (${formatSafeDate(updatedQuotation.startDate, 'dd/MM')})`;
            } else if (updatedQuotation.startDate && index > 0) {
              // Calculate sequential date for subsequent days
              const date = new Date(updatedQuotation.startDate);
              date.setDate(date.getDate() + index);
              dayTitle = `Dia ${index + 1} (${formatSafeDate(date, 'dd/MM')})`;
            }
            
            return {
              ...page,
              // Store current title as pageTitle if not already set
              pageTitle: page.pageTitle || page.title,
              // Set dayTitle and update visible title to day format
              dayTitle: dayTitle,
              title: dayTitle // For backward compatibility
            };
          });
        } else {
          // Switching back to page mode - restore original page titles
          updatedQuotation.pages = updatedQuotation.pages.map(page => {
            return {
              ...page,
              // Restore the page title from the stored pageTitle or fall back to a default
              title: page.pageTitle || page.title,
              // Keep the current dayTitle for when switching back to day mode
              dayTitle: page.dayTitle
            };
          });
        }
        
        return updatedQuotation;
      });
      return;
    }
    
    // Special handling for startDate when in day mode (to update day titles)
    if (field === 'startDate' && quotation.useDayMode) {
      updateQuotationWithBackup(prev => {
        const updatedQuotation = { ...prev, [field]: value, updatedAt: new Date() };
        
        // Only update day titles if we're in day mode and have a start date
        if (updatedQuotation.useDayMode) {
          updatedQuotation.pages = updatedQuotation.pages.map((page, index) => {
            let dayTitle = `Dia ${index + 1}`;
            
            // If we have a defined start date, calculate sequential dates
            if (value && index === 0) {
              // First day gets the exact start date
              dayTitle = `Dia ${index + 1} (${formatSafeDate(value, 'dd/MM')})`;
            } else if (value && index > 0) {
              // Calculate sequential date for subsequent days
              const date = new Date(value);
              date.setDate(date.getDate() + index);
              dayTitle = `Dia ${index + 1} (${formatSafeDate(date, 'dd/MM')})`;
            }
            
            return {
              ...page,
              title: dayTitle, // For backward compatibility
              dayTitle: dayTitle
            };
          });
        }
        
        return updatedQuotation;
      });
      return;
    }

    // Special handling for page_groups
    if (field === 'page_groups') {
      updateQuotationWithBackup(prev => ({
        ...prev,
        page_groups: value,
        updatedAt: new Date()
      }));
      return;
    }
    
    // Default handler for other fields
    updateQuotationWithBackup(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date()
    }));
  };

  // Funções para manipulação de páginas
  const addPage = () => {
    updateQuotationWithBackup(prev => {
      const newPageId = `page_${Date.now()}`;
      const allPages = [...prev.pages];
      
      // Determinar a ordem global
      const globalOrder = allPages.length > 0 
        ? Math.max(...allPages.map(p => p.globalOrder || 0)) + 1 
        : 0;
      
      // Criar nova página
    const newPage: QuotationPage = {
        id: newPageId,
        title: `Página ${allPages.length + 1}`,
      description: '',
      blocks: [],
        images: [],
        showFlights: true,
        showAccommodations: true,
        showTours: true,
        showTransportations: true,
        showRestaurants: true,
        showMap: true,
      flights: [],
      accommodations: [],
      tours: [],
      transportations: [],
      restaurants: [],
      flightEvents: [],
      accommodationEvents: [],
        globalOrder: globalOrder,
      };

      // Garantir que page_groups existe e tem estrutura válida
      const currentPageGroups = prev.page_groups || { groups: [], ungrouped: [] };
      
      // Adicionar nova página ao ungrouped do page_groups
      const updatedPageGroups = {
        ...currentPageGroups,
        ungrouped: [...(currentPageGroups.ungrouped || []), newPageId]
      };
      
      return {
      ...prev,
      pages: [...prev.pages, newPage],
      page_groups: updatedPageGroups,
      updatedAt: new Date()
      };
    });
  };
  
  const updatePage = (pageId: string, field: keyof QuotationPage, value: any) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => 
          page.id === pageId 
          ? { ...page, [field]: value } 
            : page
      );
      
      return {
          ...prev,
        pages: updatedPages,
          updatedAt: new Date()
      };
    });
  };

  const deletePage = (pageId: string) => {
    if (!pageId) {
      return;
    }
    
    updateQuotationWithBackup(prev => {
      const filteredPages = prev.pages.filter(page => page.id !== pageId);
      
      return {
        ...prev,
        pages: filteredPages,
        updatedAt: new Date()
      };
    });
  };

  const duplicatePage = async (pageId: string) => {
    try {
      updateQuotationWithBackup(prev => {
        // Encontrar a página a ser duplicada
        const pageToDuplicate = prev.pages.find(page => page.id === pageId);
        if (!pageToDuplicate) return prev;
        
        // Criar uma cópia profunda da página
        const pageCopy = JSON.parse(JSON.stringify(pageToDuplicate));
        
        // Atualizar IDs para evitar conflitos
        const newPageId = `page_${Date.now()}`;
        pageCopy.id = newPageId;
        pageCopy.title = `${pageCopy.title} (cópia)`;
        
        // Garantir que a propriedade showMap seja preservada
        pageCopy.showMap = pageToDuplicate.showMap !== false;
        
        // ✅ CORREÇÃO: Preservar URLs de imagens durante duplicação (não fazer upload)
        // As imagens da página original devem manter suas URLs originais
        if (pageCopy.images && Array.isArray(pageCopy.images)) {
          console.log('🔗 Preserving page images during duplication:', pageCopy.images.length, 'images');
          // URLs são preservadas automaticamente (não precisamos de processamento adicional)
        }
        
        // Atualizar IDs de todos os blocos e preservar suas imagens
        pageCopy.blocks = pageCopy.blocks.map((block: QuotationBlock) => {
          const newBlock = {
            ...block,
            id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          };
          
          // ✅ CORREÇÃO: Preservar URLs de imagens dos blocos durante duplicação
          if (newBlock.images && Array.isArray(newBlock.images)) {
            console.log('🔗 Preserving block images during duplication:', newBlock.images.length, 'images');
            // URLs são preservadas automaticamente (não precisamos de processamento adicional)
          }
          
          return newBlock;
        });
        
        // Determinar a ordem global
        const globalOrder = Math.max(...prev.pages.map(p => p.globalOrder || 0)) + 1;
        pageCopy.globalOrder = globalOrder;
        
        console.log('✅ Page duplicated successfully with preserved image URLs');
        
        // Retornar quotação atualizada com a nova página
        return {
          ...prev,
          pages: [...prev.pages, pageCopy],
          updatedAt: new Date()
        };
      });
      
      toast({
        title: "Página duplicada",
        description: "A página foi duplicada com sucesso (URLs de imagens preservadas).",
        duration: 3000,
      });
    } catch (error) {
      console.error('Erro ao duplicar página:', error);
      toast({
        title: "Erro ao duplicar",
        description: "Não foi possível duplicar a página. Tente novamente.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Função para copiar uma página para outra cotação
  const copyPageToQuotation = async (pageId: string, targetQuotationId: string) => {
    try {
      console.log('[copyPageToQuotation] INICIANDO com parâmetros:', { pageId, targetQuotationId });
      
      // 1. Encontrar a página na cotação atual
      const pageToCopy = quotation.pages.find(page => page.id === pageId);
      
      console.log('[copyPageToQuotation] Procurando página na cotação atual:', { 
        encontrada: !!pageToCopy,
        totalPaginas: quotation.pages.length,
        idsPaginas: quotation.pages.map(p => p.id)
      });
      
      if (!pageToCopy) {
        console.error('[copyPageToQuotation] ERRO: Página não encontrada');
        return false;
      }
      
      console.log('[copyPageToQuotation] Página encontrada:', { 
        pageId: pageToCopy.id, 
        title: pageToCopy.title,
        blocksCount: pageToCopy.blocks?.length || 0,
        flightsCount: pageToCopy.flights?.length || 0,
        accommodationsCount: pageToCopy.accommodations?.length || 0
      });
      
      // 2. Buscar a cotação de destino
      console.log('[copyPageToQuotation] Buscando cotação de destino:', targetQuotationId);
      const targetQuotation = await quotationService.getQuotationById(targetQuotationId);
      
      console.log('[copyPageToQuotation] Resultado da busca da cotação de destino:', {
        encontrada: !!targetQuotation,
        id: targetQuotation?.id,
        title: targetQuotation?.title,
        pagesCount: targetQuotation?.pages?.length || 0
      });
      
      if (!targetQuotation) {
        console.error('[copyPageToQuotation] ERRO: Cotação de destino não encontrada');
        return false;
      }
      
      // 3. Criar uma cópia da página com um novo ID
      const newId = uuidv4();
      console.log('[copyPageToQuotation] Gerando novo ID para a página:', newId);
      
      const newPage = {
        ...pageToCopy,
        id: newId,
        globalOrder: (targetQuotation.pages?.length || 0) + 1
      };
      
      console.log('[copyPageToQuotation] Nova página criada:', { 
        newId: newPage.id,
        title: newPage.title,
        globalOrder: newPage.globalOrder,
        blocksCount: newPage.blocks?.length || 0
      });
      
      // 4. Adicionar a página copiada à cotação de destino
      const updatedPages = [...(targetQuotation.pages || []), newPage];
      
      console.log('[copyPageToQuotation] Adicionando página à cotação de destino:', { 
        pagesAntes: targetQuotation.pages?.length || 0,
        pagesDepois: updatedPages.length
      });
      
      // 5. Atualizar a cotação de destino no Supabase
      console.log('[copyPageToQuotation] Preparando chamada para updateQuotation:', {
        targetQuotationId: targetQuotation.id,
        updateParams: {
          pagesCount: updatedPages.length,
          firstPageId: updatedPages[0]?.id,
          lastPageId: updatedPages[updatedPages.length - 1]?.id
        }
      });
      
      try {
        const success = await quotationService.updateQuotation(targetQuotation.id, {
          ...targetQuotation,
          pages: updatedPages
        });
        
        console.log('[copyPageToQuotation] Resultado do updateQuotation:', {
          success: !!success,
          returnedId: success?.id,
          returnedPagesCount: success?.pages?.length || 0
        });
        
        toast({
          title: 'Página copiada',
          description: 'A página foi copiada para a cotação de destino.',
          variant: 'default'
        });
        
        console.log('[copyPageToQuotation] OPERAÇÃO CONCLUÍDA COM SUCESSO');
        return true;
      } catch (updateError) {
        console.error('[copyPageToQuotation] ERRO ao chamar updateQuotation:', updateError);
        console.error('[copyPageToQuotation] Detalhes do erro:', {
          mensagem: updateError.message,
          codigo: updateError.code,
          stack: updateError.stack
        });
        throw updateError; // Propagar o erro para que seja capturado pelo catch externo
      }
    } catch (error) {
      console.error('[copyPageToQuotation] ERRO GLOBAL na operação de cópia:', error);
      console.error('[copyPageToQuotation] Stack trace completo:', error.stack);
      
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar a página. Tente novamente.',
        variant: 'destructive'
      });
      
      return false;
    }
  };

  const reorderPages = (sourceIndex: number, destinationIndex: number) => {
    updateQuotationWithBackup(prev => {
      const result = [...prev.pages];
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(destinationIndex, 0, removed);
      
      // Atualizar globalOrder e títulos para refletir a nova ordem
      const updatedPages = result.map((page, index) => {
        const updatedPage = {
          ...page,
          globalOrder: index
        };

        // Se estiver no modo de dias, atualizar o título
        if (prev.useDayMode) {
          let dayTitle = `Dia ${index + 1}`;
          
          // Se temos uma data de início definida, calcular datas sequenciais
          if (prev.startDate && index === 0) {
            // Primeiro dia recebe a data exata de início
            dayTitle = `Dia ${index + 1} (${formatSafeDate(prev.startDate, 'dd/MM')})`;
          } else if (prev.startDate && index > 0) {
            // Calcular data sequencial para os dias subsequentes
            const date = new Date(prev.startDate);
            date.setDate(date.getDate() + index);
            dayTitle = `Dia ${index + 1} (${formatSafeDate(date, 'dd/MM')})`;
          }

          updatedPage.title = dayTitle;
          updatedPage.dayTitle = dayTitle;
        }

        return updatedPage;
      });

      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  // Funções para manipulação de blocos
  const addBlock = (pageId: string) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => {
        if (page.id === pageId) {
          const blocks = page.blocks || [];
          const newBlockId = `block_${Date.now()}`;
          
          // Criar novo bloco
    const newBlock: QuotationBlock = {
            id: newBlockId,
            title: '', // Nome vazio para que o componente aplique o fallback
      location: '',
      description: '',
            images: [],
      isCollapsed: false
    };

          return {
            ...page,
            blocks: [...blocks, newBlock]
          };
        }
        return page;
      });
      
      return {
      ...prev,
        pages: updatedPages,
      updatedAt: new Date()
      };
    });
  };

  const updateBlock = (pageId: string, blockId: string, field: keyof QuotationBlock, value: any) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => {
        if (page.id === pageId) {
          const updatedBlocks = page.blocks.map(block => 
                block.id === blockId 
                  ? { ...block, [field]: value } 
                  : block
          );
          
          return {
            ...page,
            blocks: updatedBlocks
          };
        }
        return page;
      });
      
      return {
      ...prev,
        pages: updatedPages,
      updatedAt: new Date()
      };
    });
  };

  const deleteBlock = (pageId: string, blockId: string) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => {
        if (page.id === pageId) {
          const filteredBlocks = page.blocks.filter(block => block.id !== blockId);
          
          return {
            ...page,
            blocks: filteredBlocks
          };
        }
        return page;
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  const duplicateBlock = (pageId: string, blockId: string) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => {
        if (page.id === pageId) {
          // Encontrar o bloco a ser duplicado
          const blockToDuplicate = page.blocks.find(block => block.id === blockId);
          if (!blockToDuplicate) return page;
          
          // Criar uma cópia profunda do bloco
          const blockCopy = JSON.parse(JSON.stringify(blockToDuplicate));
          
          // Atualizar ID para evitar conflitos
          blockCopy.id = `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          // If the original has a title, append (cópia), otherwise leave empty for the fallback pattern
          blockCopy.title = blockToDuplicate.title ? `${blockToDuplicate.title} (cópia)` : '';
          
          // Retornar página atualizada com o novo bloco
          return {
            ...page,
            blocks: [...page.blocks, blockCopy]
          };
        }
        return page;
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  const reorderBlocks = (pageId: string, sourceIndex: number, destinationIndex: number) => {
    updateQuotationWithBackup(prev => {
      const updatedPages = prev.pages.map(page => {
        if (page.id === pageId) {
          const result = [...page.blocks];
          const [removed] = result.splice(sourceIndex, 1);
          result.splice(destinationIndex, 0, removed);
          
          return {
            ...page,
            blocks: result
          };
        }
        return page;
      });

      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  // Funções para manipulação de itens (tours, acomodações, voos, etc.)
  const moveTourToPage = (sourcePageId: string, tourId: string, targetPageId: string) => {
    updateQuotationWithBackup(prev => {
      // Encontrar página fonte e passeio
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      if (!sourcePage || !sourcePage.tours) return prev;
      
      const tourToMove = sourcePage.tours.find(t => t.id === tourId);
      if (!tourToMove) return prev;

      // Criar cópia profunda do passeio
      const tourCopy = JSON.parse(JSON.stringify(tourToMove));
      
      // Remover o passeio da página fonte
      const updatedSourcePage = {
        ...sourcePage,
        tours: sourcePage.tours.filter(t => t.id !== tourId)
      };
      
      // Adicionar o passeio à página de destino
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          return {
            ...page,
            tours: [...(page.tours || []), tourCopy]
          };
        }
        
        return page;
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  const moveRestaurantToPage = (sourcePageId: string, restaurantId: string, targetPageId: string) => {
    console.log("moveRestaurantToPage called with:", { sourcePageId, restaurantId, targetPageId });
    
    updateQuotationWithBackup(prev => {
      // Find source page and restaurant
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      console.log("Source page found:", { 
        pageId: sourcePageId, 
        pageFound: !!sourcePage, 
        hasRestaurants: !!(sourcePage?.restaurants), 
        restaurantsCount: sourcePage?.restaurants?.length 
      });
      
      if (!sourcePage || !sourcePage.restaurants) return prev;
      
      const restaurantToMove = sourcePage.restaurants.find(r => r.id === restaurantId);
      console.log("Restaurant to move:", { 
        found: !!restaurantToMove, 
        restaurant: restaurantToMove 
      });
      
      if (!restaurantToMove) return prev;

      // Criar cópia profunda do restaurante
      const restaurantCopy = JSON.parse(JSON.stringify(restaurantToMove));
      
      // Remover o restaurante da página fonte
      const updatedSourcePage = {
        ...sourcePage,
        restaurants: sourcePage.restaurants.filter(r => r.id !== restaurantId)
      };
      
      const targetPage = prev.pages.find(p => p.id === targetPageId);
      console.log("Target page found:", { 
        pageId: targetPageId, 
        pageFound: !!targetPage, 
        hasRestaurants: !!(targetPage?.restaurants), 
        restaurantsCount: targetPage?.restaurants?.length
      });
      
      // Adicionar o restaurante à página de destino
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          console.log("Updating source page: removing restaurant");
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          console.log("Updating target page: adding restaurant");
          const updatedPage = {
            ...page,
            restaurants: [...(page.restaurants || []), restaurantCopy]
          };
          console.log("Updated target page restaurants count:", updatedPage.restaurants.length);
          return updatedPage;
        }
        
        return page;
      });
      
      console.log("Restaurant move complete");
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  const moveAccommodationToPage = (sourcePageId: string, accommodationId: string, targetPageId: string) => {
    updateQuotationWithBackup(prev => {
      // Find source page and accommodation
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      if (!sourcePage || !sourcePage.accommodations) return prev;
      
      const accommodationToMove = sourcePage.accommodations.find(a => a.id === accommodationId);
      if (!accommodationToMove) return prev;

      // Create deep copy of the accommodation
      const accommodationCopy = JSON.parse(JSON.stringify(accommodationToMove));
      
      // Remove accommodation from source page
      const updatedSourcePage = {
        ...sourcePage,
        accommodations: sourcePage.accommodations.filter(a => a.id !== accommodationId)
      };
      
      // Add accommodation to destination page
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          return {
            ...page,
            accommodations: [...(page.accommodations || []), accommodationCopy]
          };
        }
        
        return page;
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  const moveFlightEventToPage = function moveFlightEventToPageFunction(sourcePageId: string, flightEventId: string, targetPageId: string) {
    console.log('[useQuotationForm] Função moveFlightEventToPage chamada:', {
      sourcePageId,
      flightEventId,
      targetPageId,
      funcionType: typeof moveFlightEventToPage,
      stackTrace: new Error().stack
    });
    
    updateQuotationWithBackup(prev => {
      // Find source page and flight event
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      if (!sourcePage || !sourcePage.flightEvents) {
        console.error('[useQuotationForm] Página de origem não encontrada ou não tem eventos:', {
          sourcePageId,
          paginasDisponiveis: prev.pages.map(p => p.id),
          sourcePage
        });
        return prev;
      }
      
      const flightEventToMove = sourcePage.flightEvents.find(e => e.id === flightEventId);
      if (!flightEventToMove) {
        console.error('[useQuotationForm] Evento de voo não encontrado:', {
          flightEventId,
          eventosDisponiveis: sourcePage.flightEvents?.map(e => e.id)
        });
        return prev;
      }

      // Create deep copy of the flight event
      const flightEventCopy = JSON.parse(JSON.stringify(flightEventToMove));
      
      console.log('[useQuotationForm] Evento de voo encontrado, pronto para mover:', {
        sourcePageId,
        targetPageId,
        flightEvent: flightEventCopy
      });
      
      // Remove flight event from source page
      const updatedSourcePage = {
        ...sourcePage,
        flightEvents: sourcePage.flightEvents.filter(e => e.id !== flightEventId)
      };
      
      // Add flight event to destination page
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          console.log('[useQuotationForm] Atualizando página de origem', {
            pageId: page.id,
            eventosAnteriores: sourcePage.flightEvents?.length,
            eventosNovos: updatedSourcePage.flightEvents.length
          });
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          console.log('[useQuotationForm] Atualizando página de destino', {
            pageId: page.id,
            eventosAnteriores: page.flightEvents?.length || 0,
            eventosNovos: (page.flightEvents?.length || 0) + 1
          });
          return {
            ...page,
            flightEvents: [...(page.flightEvents || []), flightEventCopy]
          };
        }
        
        return page;
      });
      
      console.log('[useQuotationForm] Atualizando cotação com páginas atualizadas', {
        paginasAtualizadas: updatedPages.length
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
    
    console.log('[useQuotationForm] Função moveFlightEventToPage concluída');
  };

  const moveAccommodationEventToPage = function moveAccommodationEventToPageFunction(sourcePageId: string, accommodationEventId: string, targetPageId: string) {
    console.log('[useQuotationForm] Função moveAccommodationEventToPage chamada:', {
      sourcePageId,
      accommodationEventId,
      targetPageId,
      funcionType: typeof moveAccommodationEventToPage,
      stackTrace: new Error().stack
    });
    
    updateQuotationWithBackup(prev => {
      // Find source page and accommodation event
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      if (!sourcePage || !sourcePage.accommodationEvents) {
        console.error('[useQuotationForm] Página de origem não encontrada ou não tem eventos de hospedagem:', {
          sourcePageId,
          paginasDisponiveis: prev.pages.map(p => p.id),
          sourcePage
        });
        return prev;
      }
      
      const accommodationEventToMove = sourcePage.accommodationEvents.find(e => e.id === accommodationEventId);
      if (!accommodationEventToMove) {
        console.error('[useQuotationForm] Evento de hospedagem não encontrado:', {
          accommodationEventId,
          eventosDisponiveis: sourcePage.accommodationEvents?.map(e => e.id)
        });
        return prev;
      }

      // Create deep copy of the accommodation event
      const accommodationEventCopy = JSON.parse(JSON.stringify(accommodationEventToMove));
      
      console.log('[useQuotationForm] Evento de hospedagem encontrado, pronto para mover:', {
        sourcePageId,
        targetPageId,
        accommodationEvent: accommodationEventCopy
      });
      
      // Remove accommodation event from source page
      const updatedSourcePage = {
        ...sourcePage,
        accommodationEvents: sourcePage.accommodationEvents.filter(e => e.id !== accommodationEventId)
      };
      
      // Add accommodation event to destination page
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          console.log('[useQuotationForm] Atualizando página de origem', {
            pageId: page.id,
            eventosAnteriores: sourcePage.accommodationEvents?.length,
            eventosNovos: updatedSourcePage.accommodationEvents.length
          });
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          console.log('[useQuotationForm] Atualizando página de destino', {
            pageId: page.id,
            eventosAnteriores: page.accommodationEvents?.length || 0,
            eventosNovos: (page.accommodationEvents?.length || 0) + 1
          });
          return {
            ...page,
            accommodationEvents: [...(page.accommodationEvents || []), accommodationEventCopy]
          };
        }
        
        return page;
      });
      
      console.log('[useQuotationForm] Atualizando cotação com páginas atualizadas', {
        paginasAtualizadas: updatedPages.length
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
    
    console.log('[useQuotationForm] Função moveAccommodationEventToPage concluída');
  };

  // Adicionar a função moveTransportationToPage junto com as outras funções de movimento
  const moveTransportationToPage = (sourcePageId: string, transportationId: string, targetPageId: string): boolean => {
    try {
      updateQuotationWithBackup(prev => {
        // Encontrar página de origem e o transporte
        const sourcePage = prev.pages.find(p => p.id === sourcePageId);
        if (!sourcePage || !Array.isArray(sourcePage.transportations)) {
          console.log("[moveTransportationToPage] Source page not found or transportations array missing", {
            sourcePageId,
            sourcePageExists: Boolean(sourcePage),
            transportationsExists: sourcePage && Array.isArray(sourcePage.transportations)
          });
          return prev; // Retornar state sem alterações
        }
        
        // Encontrar o transporte a ser movido
        const transportationToMove = sourcePage.transportations.find(t => t.id === transportationId);
        if (!transportationToMove) {
          console.log("[moveTransportationToPage] Transportation not found", { transportationId });
          return prev; // Retornar state sem alterações
        }

        // Criar cópia profunda do transporte
        const transportationCopy = JSON.parse(JSON.stringify(transportationToMove));
        
        // Remover transporte da página de origem
        const updatedSourcePage = {
          ...sourcePage,
          transportations: sourcePage.transportations.filter(t => t.id !== transportationId)
        };
        
        // Verificar página de destino e adicionar o transporte
        const updatedPages = prev.pages.map(page => {
          if (page.id === sourcePageId) {
            return updatedSourcePage;
          }
          
          if (page.id === targetPageId) {
            // Garantir que a matriz existe antes de adicionar
            const existingTransportations = Array.isArray(page.transportations) ? page.transportations : [];
            return {
              ...page,
              transportations: [...existingTransportations, transportationCopy]
            };
          }
          
          return page;
        });
        
        // Logging para debugging
        console.log("[moveTransportationToPage] Successfully moved transportation", {
          transportationId,
          sourcePageId,
          targetPageId
        });
        
        // Retornar state atualizado
        return {
          ...prev,
          pages: updatedPages,
          updatedAt: new Date()
        };
      });
      
      return true; // Indicar sucesso
    } catch (error) {
      console.error("[moveTransportationToPage] Error moving transportation:", error);
      return false; // Indicar falha
    }
  };

  // Implementação da função moveFlightToPage
  const moveFlightToPage = (sourcePageId: string, flightId: string, targetPageId: string) => {
    console.log('[useQuotationForm] Função moveFlightToPage chamada:', {
      sourcePageId,
      flightId,
      targetPageId,
      functionType: typeof moveFlightToPage,
      stackTrace: new Error().stack
    });
    
    updateQuotationWithBackup(prev => {
      // Find source page and flight
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      if (!sourcePage || !sourcePage.flights) {
        console.error('[useQuotationForm] Página de origem não encontrada ou não tem voos:', {
          sourcePageId,
          paginasDisponiveis: prev.pages.map(p => p.id),
          sourcePage
        });
        return prev;
      }
      
      const flightToMove = sourcePage.flights.find(f => f.id === flightId);
      if (!flightToMove) {
        console.error('[useQuotationForm] Voo não encontrado:', {
          flightId,
          voosDisponiveis: sourcePage.flights?.map(f => f.id)
        });
        return prev;
      }

      // Create deep copy of the flight
      const flightCopy = JSON.parse(JSON.stringify(flightToMove));
      
      console.log('[useQuotationForm] Voo encontrado, pronto para mover:', {
        sourcePageId,
        targetPageId,
        flight: flightCopy
      });
      
      // Remove flight from source page
      const updatedSourcePage = {
        ...sourcePage,
        flights: sourcePage.flights.filter(f => f.id !== flightId)
      };
      
      // Add flight to destination page
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          console.log('[useQuotationForm] Atualizando página de origem', {
            pageId: page.id,
            voosAnteriores: sourcePage.flights?.length,
            voosNovos: updatedSourcePage.flights.length
          });
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          console.log('[useQuotationForm] Atualizando página de destino', {
            pageId: page.id,
            voosAnteriores: page.flights?.length || 0,
            voosNovos: (page.flights?.length || 0) + 1
          });
          return {
            ...page,
            flights: [...(page.flights || []), flightCopy]
          };
        }
        
        return page;
      });
      
      console.log('[useQuotationForm] Atualizando cotação com páginas atualizadas', {
        paginasAtualizadas: updatedPages.length
      });
      
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
    
    console.log('[useQuotationForm] Função moveFlightToPage concluída');
  };

  // Após a função moveFlightToPage, adicionar a função moveBlockToPage
  const moveBlockToPage = (sourcePageId: string, blockId: string, targetPageId: string) => {
    console.log("[useQuotationForm] moveBlockToPage chamado:", { sourcePageId, blockId, targetPageId });
    
    updateQuotationWithBackup(prev => {
      // Encontrar página fonte e bloco
      const sourcePage = prev.pages.find(p => p.id === sourcePageId);
      console.log("[useQuotationForm] Página fonte encontrada:", { 
        pageId: sourcePageId, 
        pageFound: !!sourcePage, 
        hasBlocks: !!(sourcePage?.blocks), 
        blocksCount: sourcePage?.blocks?.length 
      });
      
      if (!sourcePage || !sourcePage.blocks) return prev;
      
      const blockToMove = sourcePage.blocks.find(b => b.id === blockId);
      console.log("[useQuotationForm] Bloco para mover:", { 
        found: !!blockToMove, 
        block: blockToMove 
      });
      
      if (!blockToMove) return prev;

      // Criar cópia profunda do bloco
      const blockCopy = JSON.parse(JSON.stringify(blockToMove));
      
      // Remover o bloco da página fonte
      const updatedSourcePage = {
        ...sourcePage,
        blocks: sourcePage.blocks.filter(b => b.id !== blockId)
      };
      
      const targetPage = prev.pages.find(p => p.id === targetPageId);
      console.log("[useQuotationForm] Página destino encontrada:", { 
        pageId: targetPageId, 
        pageFound: !!targetPage, 
        hasBlocks: !!(targetPage?.blocks), 
        blocksCount: targetPage?.blocks?.length
      });
      
      // Adicionar o bloco à página de destino
      const updatedPages = prev.pages.map(page => {
        if (page.id === sourcePageId) {
          console.log("[useQuotationForm] Atualizando página fonte: removendo bloco");
          return updatedSourcePage;
        }
        
        if (page.id === targetPageId) {
          console.log("[useQuotationForm] Atualizando página destino: adicionando bloco");
          const updatedPage = {
            ...page,
            blocks: [...(page.blocks || []), blockCopy]
          };
          console.log("[useQuotationForm] Contagem de blocos da página destino atualizada:", updatedPage.blocks.length);
          return updatedPage;
        }
        
        return page;
      });
      
      console.log("[useQuotationForm] Movimento de bloco concluído");
      return {
        ...prev,
        pages: updatedPages,
        updatedAt: new Date()
      };
    });
  };

  // Armazenar referência global da função
  globalMoveBlockToPageRef = moveBlockToPage;

  // Atualizar as referências globais para as funções
  globalMoveFlightEventToPage = moveFlightEventToPage;
  globalMoveAccommodationEventToPage = moveAccommodationEventToPage;
  globalMoveAccommodationToPageRef = moveAccommodationToPage;
  globalMoveTransportationToPageRef = moveTransportationToPage;
  globalMoveFlightToPageRef = moveFlightToPage;

  // Função para salvar a cotação no Supabase
  const saveQuotation = async () => {
    try {
      // Debug do campo notes especificamente
      console.log('[useQuotationForm.saveQuotation] VERIFICAÇÃO DETALHADA DE NOTES:', {
        notesOriginal: quotation.notes,
        notesLength: quotation.notes?.length || 0,
        notesIsHTML: quotation.notes?.includes('<') || false,
        notesIsEmpty: !quotation.notes || quotation.notes.trim() === '',
        notesVazio: quotation.notes === '',
        notesNull: quotation.notes === null,
        notesUndefined: quotation.notes === undefined,
        primeirosCaracteres: quotation.notes?.substring(0, 50) || 'N/A'
      });
      
      // Log detalhado para diagnóstico dos grupos antes de salvar
      console.log('[useQuotationForm.saveQuotation] Verificando grupos de recomendações antes de salvar:', {
        totalGroups: quotation.recommendationGroups?.length || 0,
        detalhesGrupos: quotation.recommendationGroups?.map(g => ({
          id: g.id,
          title: g.title,
          hasDescription: !!g.description,
          descriptionLength: g.description?.length || 0,
          descriptionSample: g.description?.substring(0, 30) || 'N/A'
        })) || []
      });
      
      // Log detalhado da cotação atual para diagnóstico
      console.log('[useQuotationForm.saveQuotation] Valores atuais da cotação:', {
        startDate: quotation.startDate ? {
          tipo: typeof quotation.startDate,
          valor: String(quotation.startDate),
          isDate: quotation.startDate instanceof Date,
          isString: typeof quotation.startDate === 'string'
        } : 'nulo',
        endDate: quotation.endDate ? {
          tipo: typeof quotation.endDate,
          valor: String(quotation.endDate),
          isDate: quotation.endDate instanceof Date,
          isString: typeof quotation.endDate === 'string'
        } : 'nulo',
      });
      
      // Garantir que temos valores de data válidos antes de salvar
      const startDateForSave = quotation.startDate ? 
        (typeof quotation.startDate === 'string' 
          ? quotation.startDate 
          : new Date(quotation.startDate).toISOString()) 
        : null;
      
      const endDateForSave = quotation.endDate ? 
        (typeof quotation.endDate === 'string' 
          ? quotation.endDate 
          : new Date(quotation.endDate).toISOString()) 
        : null;
      
      // Obter a cotação atual do banco para garantir uma mesclagem completa
      const currentQuotation = await quotationService.getQuotationById(quotation.id);
      
      // SEGURANÇA ADICIONAL: Verificar e preservar campos críticos
      // Preservar notas se estiverem vazias no estado atual mas existem no banco
      if (!quotation.notes || quotation.notes.trim() === '') {
        if (currentQuotation && currentQuotation.notes && currentQuotation.notes.trim() !== '') {
          console.log('[useQuotationForm.saveQuotation] Preservando notas do banco de dados!');
          quotation.notes = currentQuotation.notes;
        }
      }
      
      // Preservar grupos de acomodação se vazios mas existem no banco
      if (!quotation.accommodationGroups || quotation.accommodationGroups.length === 0) {
        if (currentQuotation && currentQuotation.accommodation_groups && 
            Array.isArray(currentQuotation.accommodation_groups) && 
            currentQuotation.accommodation_groups.length > 0) {
          console.log('[useQuotationForm.saveQuotation] Preservando grupos de acomodação do banco!');
          quotation.accommodationGroups = currentQuotation.accommodation_groups;
        }
      }
      
      // Preservar grupos de voo se vazios mas existem no banco
      if (!quotation.flightGroups || quotation.flightGroups.length === 0) {
        if (currentQuotation && currentQuotation.flight_groups && 
            Array.isArray(currentQuotation.flight_groups) && 
            currentQuotation.flight_groups.length > 0) {
          console.log('[useQuotationForm.saveQuotation] Preservando grupos de voo do banco!');
          quotation.flightGroups = currentQuotation.flight_groups;
        }
      }
      
      // Converter para o formato do Supabase, mesclando com os dados atuais do banco
      const supabaseQuotation: any = {
        ...currentQuotation, // Preservar todos os campos do banco
        // Sobrescrever com os campos atualizados
        title: quotation.title,
        client: quotation.client,
        trip_id: quotation.tripId,
        destination: quotation.destination,
        // Usar as variáveis preparadas para as datas
        start_date: startDateForSave,
        end_date: endDateForSave,
        notes: quotation.notes || currentQuotation?.notes || '',
        pages: quotation.pages || [],
        cover_images: quotation.coverImages || [],
        // Garantir que os grupos de recomendações sejam incluídos corretamente
        recommendation_groups: quotation.recommendationGroups ? quotation.recommendationGroups.map(group => ({
          id: group.id,
          title: group.title,
          description: group.description || '', // Garantir que a descrição seja incluída explicitamente
          order: group.order,
          itemIds: group.itemIds
        })) : [],
        // Campos de configuração de acomodações
        accommodation_groups: quotation.accommodationGroups || currentQuotation?.accommodation_groups || [],
        use_custom_accommodation_groups: quotation.useCustomAccommodationGroups !== undefined ? 
            quotation.useCustomAccommodationGroups : currentQuotation?.use_custom_accommodation_groups || false,
        accommodations_description: quotation.accommodationsDescription || currentQuotation?.accommodations_description || '',
        show_accommodations_map_section: quotation.showAccommodationsMapSection !== undefined ?
            quotation.showAccommodationsMapSection : currentQuotation?.show_accommodations_map_section || false,
        // Campos de configuração de voos
        flight_groups: quotation.flightGroups || currentQuotation?.flight_groups || [],
        use_custom_flight_groups: quotation.useCustomFlightGroups !== undefined ?
            quotation.useCustomFlightGroups : currentQuotation?.use_custom_flight_groups || false,
        flights_description: quotation.flightsDescription || currentQuotation?.flights_description || '',
        // Configurações de visualização de abas
        show_recommendations_tab: quotation.showRecommendationsTab !== undefined ? quotation.showRecommendationsTab : false,
        recommendations_tab_name: quotation.recommendationsTabName || 'Mais Dicas',
        show_price_tab: quotation.showPriceTab !== undefined ? quotation.showPriceTab : false,
        price_tab_name: quotation.priceTabName || 'Valores',
        show_accommodations_tab: quotation.showAccommodationsTab !== undefined ? quotation.showAccommodationsTab : true, 
        accommodations_tab_name: quotation.accommodationsTabName || 'Hospedagens',
        show_flights_tab: quotation.showFlightsTab !== undefined ? quotation.showFlightsTab : true,
        flights_tab_name: quotation.flightsTabName || 'Voos',
        // === Adicionar campos da Aba Personalizada ===
        show_custom_tab: quotation.showCustomTab !== undefined ? quotation.showCustomTab : false,
        custom_tab_name: quotation.customTabName || 'Personalizada',
        custom_tab_groups: quotation.customTabGroups || currentQuotation?.custom_tab_groups || [],
        // ==============================================
        // Outras configurações de visualização
        use_day_mode: quotation.useDayMode,
        use_page_dropdown: quotation.usePageDropdown,
        show_map_lines: quotation.showMapLines,
        show_accommodations_in_itinerary: quotation.showAccommodationsInItinerary,
        show_flights_in_itinerary: quotation.showFlightsInItinerary,
        // === CORREÇÃO: Incluir o campo de detalhamento de preços ===
        price_details: quotation.priceDetails || '',
        // 4. Garantir que page_groups salva no Supabase e localStorage
        page_groups: quotation.page_groups || { groups: [], ungrouped: quotation.pages?.map(p => p.id) || [] },
      };
      
      // Log para verificar explicitamente se a descrição foi preservada no objeto final
      console.log('[useQuotationForm.saveQuotation] Verificando preservação da descrição no objeto final:', {
        totalGrupos: supabaseQuotation.recommendation_groups?.length || 0,
        primeiroGrupo: supabaseQuotation.recommendation_groups?.[0] ? {
          id: supabaseQuotation.recommendation_groups[0].id,
          title: supabaseQuotation.recommendation_groups[0].title,
          hasDescription: !!supabaseQuotation.recommendation_groups[0].description,
          descriptionLength: supabaseQuotation.recommendation_groups[0].description?.length || 0
        } : 'Sem grupos'
      });
      
      // Log para diagnóstico do objeto finalizado
      console.log('[useQuotationForm.saveQuotation] Objeto mapeado para Supabase:', {
        show_price_tab: supabaseQuotation.show_price_tab,
        price_tab_name: supabaseQuotation.price_tab_name,
        notesLength: supabaseQuotation.notes ? supabaseQuotation.notes.length : 0,
        recommendationGroups: supabaseQuotation.recommendation_groups?.map((g: any) => ({
          id: g.id, 
          title: g.title,
          hasDescription: !!g.description,
          descriptionLength: g.description ? g.description.length : 0,
          descriptionSample: g.description ? g.description.substring(0, 30) + '...' : 'N/A'
        }))
      });
      
      if (isEditing && quotation.id) {
        // Atualizar cotação existente
        await quotationService.updateQuotation(quotation.id, supabaseQuotation);
        
        // Manter a versão mais recente no localStorage
        localStorage.setItem(QUOTATION_STORAGE_KEY, JSON.stringify({ ...quotation, page_groups: quotation.page_groups || { groups: [], ungrouped: quotation.pages?.map(p => p.id) || [] } }));
      } else {
        // Caso imprevisto onde não temos id mesmo no modo de edição
        console.error("Erro ao salvar cotação: ID não disponível no modo de edição");
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar a cotação. ID não disponível.",
          variant: "destructive"
        });
        return false;
      }
      
      toast({
        title: "Cotação salva",
        description: "A cotação foi salva com sucesso.",
      });
      
      return true;
    } catch (error) {
      console.error("Erro ao salvar cotação:", error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar a cotação.",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    quotation,
    isLoading,
    canUndo,
    handleQuotationChange,
    saveQuotation,
    undo,
    migrateOrphanPages,
    addPage,
    updatePage,
    deletePage,
    duplicatePage,
    copyPageToQuotation,
    reorderPages,
    addBlock,
    updateBlock,
    deleteBlock,
    duplicateBlock,
    reorderBlocks,
    moveTourToPage,
    moveRestaurantToPage,
    moveAccommodationToPage,
    moveFlightEventToPage,
    moveAccommodationEventToPage,
    moveTransportationToPage,
    moveFlightToPage,
    moveBlockToPage,
    isEditing
  };
}

// Função global para mover eventos de voo entre páginas
export function globalMoveFlightEvent(sourcePageId: string, flightEventId: string, targetPageId: string) {
  if (globalMoveFlightEventToPage) {
    console.log('[Global] Chamando função globalMoveFlightEventToPage:', {
      sourcePageId,
      flightEventId,
      targetPageId
    });
    globalMoveFlightEventToPage(sourcePageId, flightEventId, targetPageId);
  } else {
    console.error('[Global] Função globalMoveFlightEventToPage não está disponível');
    // TODO: Adicionar algum toast de erro aqui
  }
}

// Função global para mover eventos de hospedagem entre páginas
export function globalMoveAccommodationEvent(sourcePageId: string, accommodationEventId: string, targetPageId: string) {
  if (globalMoveAccommodationEventToPage) {
    console.log('[Global] Chamando função globalMoveAccommodationEventToPage:', {
      sourcePageId,
      accommodationEventId,
      targetPageId
    });
    globalMoveAccommodationEventToPage(sourcePageId, accommodationEventId, targetPageId);
  } else {
    console.error('[Global] Função globalMoveAccommodationEventToPage não está disponível');
    // TODO: Adicionar algum toast de erro aqui
  }
}

// Replace the problematic globalMoveAccommodationToPage function
export function globalMoveAccommodationToPage(sourcePageId: string, accommodationId: string, targetPageId: string) {
  console.log('[useQuotationForm] globalMoveAccommodationToPage called:', {
    sourcePageId,
    accommodationId,
    targetPageId
  });
  
  if (globalMoveAccommodationToPageRef) {
    console.log('[Global] Chamando função globalMoveAccommodationToPageRef:', {
      sourcePageId,
      accommodationId,
      targetPageId
    });
    return globalMoveAccommodationToPageRef(sourcePageId, accommodationId, targetPageId);
  } else {
    console.error('[Global] Função moveAccommodationToPage não está disponível');
    throw new Error('moveAccommodationToPage function not available');
  }
}

// Adicionar a função global após a definição do hook
export function globalMoveTransportationToPage(sourcePageId: string, transportationId: string, targetPageId: string): boolean {
  try {
    console.log("[globalMoveTransportationToPage] Called with params:", {
      sourcePageId,
      transportationId,
      targetPageId,
      hasFunction: typeof globalMoveTransportationToPageRef === 'function'
    });
    
    if (typeof globalMoveTransportationToPageRef === 'function') {
      return globalMoveTransportationToPageRef(sourcePageId, transportationId, targetPageId);
    } else {
      console.error("[globalMoveTransportationToPage] Function reference not available");
      throw new Error("moveTransportationToPage function not available");
    }
  } catch (error) {
    console.error("[globalMoveTransportationToPage] Error:", error);
    throw error; // Re-throw para permitir tratamento pelo componente
  }
}

// Adicionar após as outras funções globais
export function globalMoveFlightToPage(sourcePageId: string, flightId: string, targetPageId: string) {
  console.log("[globalMoveFlightToPage] Called with params:", {
    sourcePageId, 
    flightId, 
    targetPageId,
    hasFunction: typeof globalMoveFlightToPageRef === 'function'
  });
  
  try {
    if (typeof globalMoveFlightToPageRef === 'function') {
      return globalMoveFlightToPageRef(sourcePageId, flightId, targetPageId);
    } else {
      console.error("[globalMoveFlightToPage] Function reference not available");
      throw new Error("Function moveFlightToPage not available");
    }
  } catch (error) {
    console.error("[globalMoveFlightToPage] Error:", error);
    throw error;
  }
}

// Adicionar a função global moveBlockToPage
export function globalMoveBlockToPage(sourcePageId: string, blockId: string, targetPageId: string) {
  console.log('[globalMoveBlockToPage] Chamado com:', { sourcePageId, blockId, targetPageId });
  
  if (globalMoveBlockToPageRef === null) {
    console.error('[globalMoveBlockToPage] Erro: Referência da função global não definida');
    throw new Error('moveBlockToPage function reference is not set');
  }
  
  try {
    globalMoveBlockToPageRef(sourcePageId, blockId, targetPageId);
    console.log('[globalMoveBlockToPage] Bloco movido com sucesso');
    return true;
  } catch (error) {
    console.error('[globalMoveBlockToPage] Erro ao mover bloco:', error);
    throw error;
  }
} 